import React from 'react';
import {
  Container,
  Typography,
  Box,
  AppBar,
  Toolbar,
  Button,
  useScrollTrigger,
  Slide,
} from '@mui/material';
import { ReactComponent as LogoTagline } from "../assets/logo.svg";

// Hide AppBar on scroll
function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const PrivacyPolicy = () => {
  return (
    <Box sx={{ minHeight: '100vh', background: '#f8f9fe' }}>
      {/* Sticky Navigation */}
      <HideOnScroll>
        <AppBar 
          position="fixed" 
          elevation={0}
          sx={{ 
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.03)'
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Button href="/" sx={{ p: 0 }}>
              <LogoTagline style={{ width: 'auto', height: '36px' }} />
            </Button>
          </Toolbar>
        </AppBar>
      </HideOnScroll>
      <Toolbar /> {/* Spacer for fixed AppBar */}

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h1" sx={{ mb: 6, fontWeight: 800 }}>
          Privacy Policy
        </Typography>

        <Typography variant="body1" sx={{ mb: 4 }}>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          1. Introduction
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          Welcome to Sashy ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our AI-powered review analysis platform.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          2. Information We Collect
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          We collect and process the following types of information:
        </Typography>
        <Box component="ul" sx={{ mb: 4, pl: 4 }}>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Business information (name, location, industry type)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Customer reviews and feedback data
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Account information (email, password, user preferences)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Usage data (how you interact with our platform)
            </Typography>
          </li>
        </Box>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          3. How We Use Your Information
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          We use your information to:
        </Typography>
        <Box component="ul" sx={{ mb: 4, pl: 4 }}>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Provide and improve our AI-powered review analysis services
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Generate insights and recommendations for your business
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Communicate with you about our services
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Ensure the security and functionality of our platform
            </Typography>
          </li>
        </Box>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          4. Data Security
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          We implement appropriate technical and organizational measures to protect your data. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          5. Data Sharing and Third Parties
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          We do not sell your personal data. We may share your information with trusted service providers who assist us in operating our platform, conducting our business, or serving our users, subject to confidentiality obligations.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          6. Your Rights
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You have the right to:
        </Typography>
        <Box component="ul" sx={{ mb: 4, pl: 4 }}>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Access your personal data
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Correct inaccurate data
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Request deletion of your data
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Object to our processing of your data
            </Typography>
          </li>
        </Box>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          7. Cookies and Tracking
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          We use cookies and similar tracking technologies to improve your experience on our platform. You can control cookie settings through your browser preferences.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          8. International Data Transfers
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          9. Changes to This Policy
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
        </Typography>

        <Typography variant="h5" sx={{ mb: 3, mt: 4, fontWeight: 700 }}>
          10. Contact Us
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          If you have any questions about this privacy policy, please contact us at{' '}
          <a href="mailto:info@sashy.ai">info@sashy.ai</a>.
        </Typography>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy; 